import { FC, useState } from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IntervalsModelType, TransportModelType } from "../../../../../common/modelTypes";
import { col4, col6 } from "../../../../../common/utils";
import ComparisonLinearProgress from "../../../../../components/charts/graph/ComparisonLinearProgress";
import NumericValueWidget from "../../../../../components/charts/NumericValueWidget";
import { MarketBaseType, MarketBaseWidget } from "../../../../../components/common/MarketBaseWidget";
import {
	EmissionsIntervalResultsDTO,
	EmissionsTransportsResultsDatumDTO,
} from "../../../../../datasources/api-client/data-contracts";
import EmissionsIntervals from "../../../components/ResultsEmissionsIntervals";

interface Props {
	transportEmissions: TransportModelType;
	keyEmissions: string;
	emissionsIntervals: IntervalsModelType;
	emissionsIntervalsPeriodType: "month" | "quarter" | "four-month" | "semester";
	setEmissionsIntervalsPeriodType: (periodType: "month" | "quarter" | "four-month" | "semester") => void;
	intervalMode: "emissions" | "energy";
	onIntervalModeChange: (mode: "emissions" | "energy") => void;
}

type EmissionType = keyof Pick<EmissionsTransportsResultsDatumDTO, "co2Ttw" | "co2Wtw">;
type EmissionKey = keyof EmissionsIntervalResultsDTO;
type IntensityKey = "intensityTtw" | "intensityWtw";

const Overview: FC<Props> = ({
	transportEmissions,
	keyEmissions,
	emissionsIntervals,
	emissionsIntervalsPeriodType,
	setEmissionsIntervalsPeriodType,
	intervalMode,
	onIntervalModeChange,
}) => {
	const { t } = useTranslation("result.transport");

	const intensityKey: IntensityKey = keyEmissions === "co2Ttw" ? "intensityTtw" : "intensityWtw";

	// Select emission for all the page
	const dropdownSelectEmissions = [
		{ key: "co2Ttw", label: t("common:ttw select") },
		{ key: "co2Wtw", label: t("common:wtw select") },
	];

	/**
	 * Resolve which key to use for the interval chart
	 * Energy → energyTtw
	 * Emissions → emission selected in overview
	 */
	const resolvedIntervalKey: EmissionKey = intervalMode === "energy" ? "energyTtw" : (keyEmissions as EmissionKey);

	// Prepare data for the chart
	const chartData = emissionsIntervals.data && keyEmissions ? emissionsIntervals.data[resolvedIntervalKey].data : [];
	const chartMeasure =
		emissionsIntervals.data && keyEmissions ? emissionsIntervals.data[resolvedIntervalKey].measure : "";

	// Calculate from and to percentages for the ComparisonLinearProgress component
	const totalEmissions = transportEmissions?.data?.total?.current?.[keyEmissions as EmissionType]?.value || 0;
	const fromEmissions = transportEmissions?.data?.greenRouter?.current[keyEmissions as EmissionType]?.value || 0;
	const toEmissions = transportEmissions?.data?.thirdParty?.current[keyEmissions as EmissionType]?.value || 0;

	const fromPercent = totalEmissions !== 0 ? (fromEmissions / totalEmissions) * 100 : 0;
	const toPercent = totalEmissions !== 0 ? (toEmissions / totalEmissions) * 100 : 0;

	const fromEmissionsDatasource = {
		name: "GreenRouter",
		value: fromPercent,
	};

	const toEmissionsDatasource = {
		name: t("third parties"),
		value: toPercent,
	};

	const isWtw = keyEmissions === "co2Wtw";

	const marketBase: MarketBaseType = {
		emissions: isWtw
			? transportEmissions.data?.total?.current.marketBasedWtw
			: transportEmissions.data?.total?.current.marketBasedTtw,
		delta: isWtw
			? transportEmissions.data?.total?.current.marketBasedWtwDelta
			: transportEmissions.data?.total?.current.marketBasedTtwDelta,
		coverage: isWtw
			? transportEmissions.data?.total?.current.marketBasedWtwCoverage
			: transportEmissions.data?.total?.current.marketBasedTtwCoverage,
		isLoading: transportEmissions.isLoading,
		onReload: transportEmissions.reload,
	};

	return (
		<Grid container spacing={2}>
			<Grid size={col4} minHeight={180}>
				<NumericValueWidget
					title={t("total transport emissions")}
					delta={transportEmissions?.data?.total?.delta?.[keyEmissions as EmissionType]}
					value={transportEmissions?.data?.total?.current[keyEmissions as EmissionType]?.value}
					measure={transportEmissions?.data?.total?.current[keyEmissions as EmissionType]?.measure}
					isLoading={transportEmissions.isLoading}
					onReload={transportEmissions.reload}
					graph={<ComparisonLinearProgress from={fromEmissionsDatasource} to={toEmissionsDatasource} />}
				/>
			</Grid>
			<Grid size={col4} minHeight={180}>
				<NumericValueWidget
					title={t("energy consumption")}
					delta={transportEmissions?.data?.total?.delta.energyTtw}
					value={transportEmissions?.data?.total?.current.energyTtw.value}
					measure={transportEmissions?.data?.total?.current.energyTtw.measure}
					isLoading={transportEmissions.isLoading}
					onReload={transportEmissions.reload}
					coverage={transportEmissions.data?.total?.current.energyTtwCoverage}
				/>
			</Grid>
			<Grid size={col4} minHeight={180}>
				<NumericValueWidget
					title={t("intensity emission")}
					delta={transportEmissions?.data?.total?.delta[intensityKey]}
					value={transportEmissions?.data?.total?.current[intensityKey].value}
					measure={transportEmissions?.data?.total?.current[intensityKey].measure}
					isLoading={transportEmissions.isLoading}
					onReload={transportEmissions.reload}
				/>
			</Grid>
			<Grid size={12} minHeight={450}>
				<EmissionsIntervals
					selectTitle={t("trend")}
					periodType={emissionsIntervalsPeriodType}
					setPeriodType={setEmissionsIntervalsPeriodType}
					isLoading={emissionsIntervals.isLoading}
					onReload={emissionsIntervals.reload}
					chartData={chartData}
					chartMeasure={chartMeasure}
					menuOptions={dropdownSelectEmissions}
					intervalMode={intervalMode}
					onIntervalModeChange={onIntervalModeChange}
				/>
			</Grid>
			<Grid size={col6} minHeight={150}>
				<NumericValueWidget
					title={t("gross weight")}
					value={transportEmissions?.data?.total?.current.weight.value}
					measure={transportEmissions?.data?.total?.current.weight.measure}
					isLoading={transportEmissions.isLoading}
					onReload={transportEmissions.reload}
				/>
			</Grid>
			<Grid size={col6} minHeight={150}>
				<NumericValueWidget
					title={t("tons km")}
					value={transportEmissions?.data?.total?.current.tkm.value}
					measure={transportEmissions?.data?.total?.current.tkm.measure}
					isLoading={transportEmissions.isLoading}
					onReload={transportEmissions.reload}
				/>
			</Grid>

			<MarketBaseWidget marketBase={marketBase} />
		</Grid>
	);
};

export default Overview;
